import { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";

import { db } from "../services/firebase";
import { useAuth } from "../context/AuthContext";
import { sortEntriesByDateAsc, isFutureEntry } from "../utils/entryDate";

// A felhasználó utolsó NAPLÓZOTT dózisa — natívon ez váltja ki a Súly
// predikátor mg-választóját (App Store 1.4.2 / APL531603): natívon nem
// „beírsz egy mg-ot, kapsz egy eredményt", hanem a saját, már rögzített
// adatodból számol.
//
// `enabled: false` esetén EGYETLEN Firestore-olvasás sem történik — a webes
// ág viselkedése így bit-azonos marad a korábbival (a 2. fázis kész-
// kritériuma: „web nulla viselkedésváltozás").
//
// Az időrend forrása a `date` mező (mikor TÖRTÉNT), nem a `createdAt` —
// `sortEntriesByDateAsc`, ugyanaz a kanonikus segédfüggvény, amit a Fooldal
// és a Statisztika is használ (CLAUDE.md 10. szabály, docs/FOGALMAK.md).
// A jövőbeli dátumú bejegyzések itt is kimaradnak, ahogy minden más
// számításból (2026-08-17 QA-lelet).

interface DoseEntry {
  dose?: number;
  date?: string;
  createdAt?: { seconds: number };
}

export interface LastLoggedDose {
  /** Az utolsó, 0-nál nagyobb naplózott dózis mg-ban, vagy null. */
  doseMg: number | null;
  loading: boolean;
}

/** Tiszta kiválasztás — tesztelhető a Firestore-tól függetlenül. */
export function selectLastLoggedDose(entries: DoseEntry[]): number | null {
  const usable = sortEntriesByDateAsc(
    entries.filter((e) => typeof e.dose === "number" && e.dose > 0 && !isFutureEntry(e))
  );
  const last = usable[usable.length - 1];
  return last?.dose ?? null;
}

export function useLastLoggedDose(enabled: boolean): LastLoggedDose {
  const { user } = useAuth();
  const [doseMg, setDoseMg] = useState<number | null>(null);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled || !user) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getDocs(query(collection(db, "entries"), where("userId", "==", user.uid)))
      .then((snap) => {
        if (cancelled) return;
        setDoseMg(selectLastLoggedDose(snap.docs.map((d) => d.data() as DoseEntry)));
      })
      .catch(() => {
        // Hálózati hiba: maradjon null → a komponens az üres állapotot
        // mutatja, nem esik vissza csendben egy kitalált dózisra
        // (docs/FOGALMAK.md #9 tanulsága).
        if (!cancelled) setDoseMg(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, user]);

  return { doseMg, loading };
}
